import { useRef } from 'react';

import { useLazyFetchGraphql } from '@zoopla/fetch-graphql';
import { useLazyOptimizelyExperiment } from '@zoopla/optimizely';
import { trackSegmentAndGAEvent } from '@zoopla/tracking';
import {
    NextBestActionEnum,
    SigninUserV3Input
} from '../../../graphql.generated';
import {
    SigninUserV2Document,
    SigninUserV2Mutation,
    SigninUserV2MutationVariables
} from '../queries/signIn.generated';
import {
    SigninUserV3Document,
    SigninUserV3Mutation,
    SigninUserV3MutationVariables
} from '../queries/signInV3.generated';
import { getNextBestAction } from '../utils/getNextBestAction';
import {
    logUserAuthFailure,
    logNetworkAuthFailure,
    trackAuthFailure,
    trackAuthSuccess,
    trackAuthRememberMe
} from './authMutationHook.tracking';
import { AuthMutationHook } from './authMutationHook.types';
import { useAuth } from './useAuth';

const checkIsSignInUserV3 = (
    data: SigninUserV3Mutation
): data is SigninUserV3Mutation => {
    return data?.signinUserV3 !== undefined;
};

export const useSignIn: AuthMutationHook<SigninUserV3Input> = ({
    trackingData
}) => {
    const hasNetworkError = useRef(false);
    const { setCurrentSession } = useAuth();
    const [isLoading, variation] = useLazyOptimizelyExperiment(
        'crme_2457_secure_sign_in'
    );
    const [isLoadingVendorNudgeExperiment, vendorLeadNudgeVariation] =
        useLazyOptimizelyExperiment('crme_2522_vendor_lead_next_best_action');

    const { lazyFetch: signInMutation } = useLazyFetchGraphql<
        SigninUserV2Mutation,
        SigninUserV2MutationVariables
    >(SigninUserV2Document, {
        onError(error) {
            if (error && 'isNetworkError' in error) {
                hasNetworkError.current = true;
            }
        }
    });

    const { lazyFetch: signInMutationV3 } = useLazyFetchGraphql<
        SigninUserV3Mutation,
        SigninUserV3MutationVariables
    >(SigninUserV3Document, {
        onError(error) {
            if (error && 'isNetworkError' in error) {
                hasNetworkError.current = true;
            }
        }
    });

    const trackError = () => {
        trackAuthFailure({
            source: 'zpg',
            type: 'signin',
            label: trackingData.label,
            segmentData: trackingData.segmentData
        });
    };

    const trackAndThrowError = (message: string, requestId?: string) => {
        trackError();

        const requestIdMessage = requestId ? ` (Request ID: ${requestId})` : '';

        throw new Error(`${message}${requestIdMessage}`);
    };

    const authenticate = async (signInInput: SigninUserV3Input) => {
        const isExperimentEnabled = !isLoading && variation === 'variation';

        const signInMutationCall = isExperimentEnabled
            ? signInMutationV3
            : signInMutation;
        const response = await signInMutationCall({ input: signInInput });

        if (hasNetworkError.current) {
            logNetworkAuthFailure({ type: 'signin' });
            return trackAndThrowError(
                'Sorry, something went wrong. Please try again. (Network error)'
            );
        }

        if (!response.data || response.errors) {
            return trackAndThrowError(
                'Sorry, something went wrong. Please try again.',
                response.extensions?.requestId
            );
        }

        const { data } = response;

        const userData = checkIsSignInUserV3(data)
            ? data.signinUserV3
            : data.signinUserV2;

        if (
            !checkIsSignInUserV3(data) &&
            data.signinUserV2?.__typename === 'AuthError'
        ) {
            if (data.signinUserV2.errorCode === 'PASSWORD') {
                return trackAndThrowError(
                    'Incorrect email or password. Please check and try again'
                );
            }

            return trackAndThrowError(
                'Sorry, something went wrong. Please try again.',
                response.extensions?.requestId
            );
        }

        if (userData?.__typename === 'AuthError') {
            if (userData.errorCode === 'INVALID_CREDENTIALS') {
                return trackAndThrowError(
                    'Incorrect email or password. Please check and try again'
                );
            }

            if (userData.errorCode === 'WEAK_PASSWORD') {
                return trackAndThrowError('Weak password, please try again.');
            }

            return trackAndThrowError(
                'Sorry, something went wrong. Please try again.',
                response.extensions?.requestId
            );
        }

        if (!userData?.user) {
            return trackAndThrowError(
                'Sorry, something went wrong. Please try again.',
                response.extensions?.requestId
            );
        }

        // This covers the case we have seen in a few incidents with sessions
        // when the authentication API works well but the user one fails
        if (!userData.user.loggedIn || !userData.user.userUuid) {
            logUserAuthFailure({
                type: 'signin',
                subtype: !userData.user.userUuid ? 'no_uuid' : 'not_logged_in'
            });

            return trackAndThrowError(
                'Sorry, something went wrong. Please try again later.',
                response.extensions?.requestId
            );
        }

        const user = userData.user;

        trackAuthSuccess({
            source: 'zpg',
            type: 'signin',
            userObject: user,
            label: trackingData.label,
            segmentData: trackingData.segmentData
        });

        if (signInInput?.rememberMe) {
            trackAuthRememberMe({
                source: 'zpg',
                type: 'signin',
                userObject: user,
                label: trackingData.label,
                segmentData: trackingData.segmentData
            });
        }

        setCurrentSession(user, false);

        const isInExperimentBucket =
            !isLoadingVendorNudgeExperiment &&
            vendorLeadNudgeVariation === 'variation';
        const isEligibleForVendorLeadNudge =
            userData.user.nextBestAction?.actions.some(
                (item) => item.action === NextBestActionEnum.VendorLeadNudge
            ) || false;

        // When a user is deemed eligible to see the Vendor lead next best action, regardless of which experiment variation they have been allocated to, it should fire GA Tracking
        if (isEligibleForVendorLeadNudge) {
            trackSegmentAndGAEvent(
                'Module Shown',
                {
                    eventCategory: 'next_best_action',
                    eventAction: 'eligible',
                    eventLabel: 'vendor_lead'
                },
                trackingData.segmentData.page
            );
        }

        // Remove vendor-lead-nudge if user is not in the experiment bucket
        // Only people eligible to see the Vendor Lead Next Best Action will be allocated to an experimentation bucket
        const nextBestActions = getNextBestAction(user).filter((action) =>
            isInExperimentBucket ? true : action !== 'confirmation'
        );

        return { ...user, actions: nextBestActions };
    };

    return authenticate;
};
